import { getCommonAxiosCommon, UrlBuilder } from 'app/helpers/MiscUtils';
import { NgAPIResponse } from 'app/types';
import axios from 'axios';
import { config } from 'config';

import { UpdateVJFPUserInput, UserStatus, VJFPUser } from '../API';
import { VJFPCache } from './CacheUtils';

const EndPoint = {
  vjfpUsers: `${config.vjfpNGRestAPI}/vjfpUsers/:organizationId`,
  vjfpUserById: `${config.vjfpNGRestAPI}/vjfpUsers/:organizationId/:id`,
  vjfpUserByEmailId: `${config.vjfpNGRestAPI}/vjfpUsers/byEmailId/:emailId`,
  vjfpUserByMultipleIds: `${config.vjfpNGRestAPI}/vjfpUsers/:organizationId?userIds=`
};

export class VJFPUserService {
  async createOrgUser (organizationId: string, user: Partial<VJFPUser>, tempPassword: string): Promise<number> {
    // 0. quick guard: ensure email exists on input
    if (!user?.emailId) {
      const e = new Error('Missing emailId');
      (e as any).code = 'VALIDATION_ERROR';
      throw e;
    }

    // 1. client-side pre-check to improve UX (server still enforces uniqueness)
    const existing = await this.getByEmail(user.emailId);
    if (existing) {
      const err = new Error('Email already exists in this platform');
      (err as any).code = 'DUPLICATE_EMAIL';
      throw err;
    }

    // 2. attempt create and map server error to structured error
    const result = await axios.post<NgAPIResponse<number>>(
      UrlBuilder(EndPoint.vjfpUsers, { organizationId }),
      { ...user, tempPassword },
      await getCommonAxiosCommon()
    );

    if (!result?.data?.isSuccess) {
      const message = (result.data.errors || []).join(',') || 'Error creating user';
      const err = new Error(message);
      // detect duplicate email server message
      if ((result.data.errors || []).some(e => String(e).toLowerCase().includes('email already exists'))) {
        (err as any).code = 'DUPLICATE_EMAIL';
      } else {
        (err as any).code = 'API_ERROR';
      }
      throw err;
    }

    return result.data.data;
  }

  // Get Users by organizationId
  async getUsers (organizationId: string): Promise<VJFPUser[]> {
    try {
      const result = await axios.get<NgAPIResponse<VJFPUser[]>>(
        UrlBuilder(EndPoint.vjfpUsers, { organizationId }),
        await getCommonAxiosCommon()
      );
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result.data?.data || [];
    } catch (err) {
      console.log(err);
      return [];
    }
  }

  async getById (organizationId: string, userId: number): Promise<VJFPUser | undefined> {
    const lastCachedUserInfo: VJFPUser = VJFPCache.getItem('vjfpUser-' + userId) as VJFPUser;
    if (
      lastCachedUserInfo?.presenceStatus &&
      lastCachedUserInfo.presenceStatus !== UserStatus.LOGGED_OUT &&
      lastCachedUserInfo.presenceStatus !== UserStatus.DISCONNECTED
    ) {
      return Promise.resolve(lastCachedUserInfo);
    }

    try {
      const result = await axios.get<NgAPIResponse<VJFPUser>>(
        UrlBuilder(EndPoint.vjfpUserById, { organizationId, id: userId.toString() }),
        await getCommonAxiosCommon()
      );
      if (
        result?.data.data?.presenceStatus &&
        (lastCachedUserInfo?.presenceStatus !== result?.data.data?.presenceStatus ||
          lastCachedUserInfo.isAvailableForChat !== result?.data.data.isAvailableForChat)
      ) {
        VJFPCache.setItem('vjfpUser-' + userId, result?.data.data);
      }
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result.data?.data as VJFPUser;
    } catch (err) {
      console.log(err);
    }
  }

  async deleteUserById (organizationId: string, userId: number) {
    await axios.delete<NgAPIResponse<VJFPUser>>(
      UrlBuilder(EndPoint.vjfpUserById, { organizationId, id: userId.toString() }),
      await getCommonAxiosCommon());
  }

  async getByEmail (emailId: string): Promise<VJFPUser | undefined> {
    try {
      const result = await axios.get<NgAPIResponse<VJFPUser>>(
        UrlBuilder(EndPoint.vjfpUserByEmailId, { emailId }),
        await getCommonAxiosCommon()
      );
      if (result?.data.data?.presenceStatus) {
        VJFPCache.setItem('vjfpUser-' + result?.data.data?.id, result?.data.data);
      }
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result.data?.data as VJFPUser;
    } catch (err) {
      console.log(err);
    }
  }

  async getByIds (organizationId: string, userIds: number[]): Promise<VJFPUser[] | undefined> {
    if (userIds?.length === 1) {
      const vjfpUser = await this.getById(organizationId, userIds[0]);
      if (vjfpUser) return [vjfpUser];
      return [];
    }
    try {
      const lastCachedMap = new Map<number, VJFPUser>();
      userIds.forEach((u) => {
        const lastCachedUserInfo = VJFPCache.getItem('vjfpUser-' + u) as VJFPUser | null;
        if (
          lastCachedUserInfo?.presenceStatus &&
          lastCachedUserInfo.presenceStatus !== UserStatus.LOGGED_OUT &&
          lastCachedUserInfo.presenceStatus !== UserStatus.DISCONNECTED
        ) {
          lastCachedMap.set(u, lastCachedUserInfo);
        }
      });
      const _userIdsNotInCache = userIds.filter((x) => !lastCachedMap.has(x));
      if (_userIdsNotInCache.length === 0) {
        return Array.from(lastCachedMap, ([key, value]) => value);
      }
      const notCachedUsersResult = await axios.get<NgAPIResponse<VJFPUser[]>>(
        `${UrlBuilder(EndPoint.vjfpUsers, { organizationId })}?ids=${_userIdsNotInCache.join(',')}`,
        await getCommonAxiosCommon()
      );
      notCachedUsersResult.data.data.forEach((_vjfpUser) => {
        VJFPCache.setItem('vjfpUser-' + _vjfpUser.id, _vjfpUser);
      });

      if (!notCachedUsersResult.data?.isSuccess) {
        console.log(notCachedUsersResult.data?.errors);
      }
      const cachedUsers = Array.from(lastCachedMap, ([key, value]) => value);
      return [...notCachedUsersResult.data.data, ...cachedUsers];
    } catch (err) {
      console.log(err);
    }
  }

  async updateAvailability (jobFairId: string, organizationId: string, id: number, isAvailableForChat: boolean): Promise<VJFPUser> {
    try {
      const axiosConfig = await getCommonAxiosCommon();

      const result = await axios.put<NgAPIResponse<VJFPUser>>(
        UrlBuilder(EndPoint.vjfpUserById, { organizationId, id: id.toString() }),
        {
          id,
          isAvailableForChat,
          presenceStatus: UserStatus.LOGGED_IN,
          lastPresenceJobFairId: jobFairId
        },
        axiosConfig
      );
      if (!result.data?.isSuccess) {
        throw new Error((result.data?.errors || []).map(e => (e as any)?.message || String(e)).join(', ') || 'Failed to update availability');
      }

      return result?.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      VJFPCache.removeItem('vjfpUser-' + id);
    }
  }

  async updateProfile (organizationId: string, id: number, input: Partial<UpdateVJFPUserInput> & { tempPassword?: string }) {
    try {
      const axiosConfig = await getCommonAxiosCommon();
      const result = await axios.put<NgAPIResponse<VJFPUser>>(
        UrlBuilder(EndPoint.vjfpUserById, { organizationId, id: id.toString() }),
        { ...input, id },
        axiosConfig
      );
      if (!result.data?.isSuccess) {
        throw new Error((result.data?.errors || []).map(e => e?.message || String(e)).join(', ') || 'Failed to update user');
      }
      return result?.data?.data;
    } catch (err) {
      console.log(err);
      throw err;
    } finally {
      VJFPCache.removeItem('vjfpUser-' + id);
    }
  }

  async updateStatus (organizationId: string, id: number, presenceStatus: UserStatus, lastPresenceJobFairId?: string) {
    try {
      const input: UpdateVJFPUserInput = {
        id,
        presenceStatus: presenceStatus
      };

      if (lastPresenceJobFairId) {
        input.lastPresenceJobFairId = lastPresenceJobFairId;
      }

      if (input.presenceStatus === UserStatus.LOGGED_OUT) {
        input.isAvailableForChat = true;
      }

      const axiosConfig = await getCommonAxiosCommon();
      const result = await axios.put<NgAPIResponse<VJFPUser>>(
        UrlBuilder(EndPoint.vjfpUserById, { organizationId, id: id.toString() }),
        input,
        axiosConfig
      );
      if (!result.data?.isSuccess) {
        console.log(result.data?.errors);
      }
      return result?.data?.data;
    } catch (err) {
      console.log(err);
    } finally {
      VJFPCache.removeItem('vjfpUser-' + id);
    }
  }

  // Check if a user exists with the given role (global, across all organizations)
  async getUserExistsWithRole (emailId: string): Promise<{ exists: boolean, roles?: string }> {
    const result = await axios.get<NgAPIResponse<{ exists: boolean, roles?: string }>>(
      `${config.vjfpNGRestAPI}/public/check-user-exists/${emailId}`
    );
    return result?.data?.data;
  }
}
